import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { SignupWarningMessages } from '@pages/Signup/Components/SignupWarningMessages'
import { CompanyDetailsTemplate } from '@pages/Signup/Pages/CompanyDetails/CompanyDetailsTemplate'

import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSignup } from '@context/SignupContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TestFormValues } from '@model/Test'

import { useFetchOne } from '@hooks/useFetch'
import { scrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'
import { TestForm, TestFormRef } from '@shared/templates/Test/TestForm'

export interface CompanyDetailsStep4FormValues {
  clientTests: {
    category: string
    testId: string
    dateTaken: string
    selectedAnswers?: any[]
    freeAnswers?: { TestQuestionId?: string; Answer?: string }[]
  }[]
  selectedAnswers?: Record<string, string | string[]>
  freeAnswers?: Record<string, string>
}

interface CompanyDetailsStep4PageProps {
  onSubmit: (values: CompanyDetailsStep4FormValues) => Promise<void>
  submitLoading?: boolean
}

export const CompanyDetailsStep4Page: React.FC<CompanyDetailsStep4PageProps> = (props) => {
  const { onSubmit } = props

  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const { signupData } = useSignup()
  const navigate = useNavigate()

  const childRef = useRef<TestFormRef>(null)

  const [formValues, setFormValues] = useState<TestFormValues>()
  const [step, setStep] = useState('')

  const getEMIRTestQuestionsCallback = useCallback(
    () => apiClient.getEMIRTestQuestions(locale),
    [apiClient, locale]
  )
  const { data, isLoading } = useFetchOne(getEMIRTestQuestionsCallback)

  const sections = data?.sections || []
  const stepIndex = sections?.findIndex((x) => x.code === step) || 0

  useEffect(() => {
    if (!step && sections.length > 0) {
      setStep(sections[0].code)
    }
  }, [sections, step])

  const handleNextStep = (values: TestFormValues) => {
    const nextSection = sections[stepIndex + 1]
    if (nextSection?.code) {
      setStep(nextSection.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const handlePrevStep = (values: TestFormValues) => {
    const prevSection = sections[stepIndex - 1]
    if (prevSection?.code) {
      setStep(prevSection.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const handleSubmit = async (values: TestFormValues) => {
    if (!data?.id) return

    await onSubmit({
      selectedAnswers: values.selectedAnswers,
      freeAnswers: values.freeAnswers,
      clientTests: [
        {
          testId: data.id,
          category: 'Corporate test',
          dateTaken: new Date().toISOString(),
          selectedAnswers: Object.values(values.selectedAnswers || {}).flat(),
          freeAnswers: Object.entries(values.freeAnswers || {}).map(([key, value]) => ({
            TestQuestionId: key,
            Answer: value,
          })),
        },
      ],
    })
  }

  const handleBack = () => {
    if (stepIndex > 0) {
      childRef.current?.goPrevStep()
    } else {
      navigate('/sign-up/company-details/regulatory')
    }
  }

  return (
    <CompanyDetailsTemplate
      lead={signupData.lead}
      step={4}
      totalSteps={signupData.totalSteps.companyDetails}
      goBack={handleBack}
    >
      <SignupWarningMessages leadTypeId={signupData.lead?.type?.id} />

      <Loading isLoading={isLoading}>
        {data && (
          <TestForm
            ref={childRef}
            data={data}
            step={step}
            values={formValues}
            onSubmit={handleSubmit}
            goPrevStep={handlePrevStep}
            goNextStep={handleNextStep}
            documents={[]}
          />
        )}
      </Loading>
    </CompanyDetailsTemplate>
  )
}
